// The ALOP module inside the DocFlow shell: its sidebar items, its pages (#/alop/...) and the module switcher.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { navigate, useAuth, useRoute } from '../store';
import { Icon } from '../ui/Icon';
import type { Task } from './types';
import { useAlopMe } from './types';
import { Dashboard, Tasks } from './Home';
import { Dossiers, EInvoices, Orders } from './Lists';
import { Dossier } from './Dossier';
import { Budget } from './Budget';
import { Settings } from './Settings';
import { DfForm } from './DfForm';
import { InvestmentsMap } from './Site';

/** The user can open the ALOP module (one of the four ALOP roles, or administrator). */
export function hasAlop(roles: string[]) { return roles.some(r => r === 'ADMIN' || r.startsWith('ALOP_')); }

export const ALOP_TITLES: Record<string, string> = { tasks: 'De făcut', dashboard: 'Execuție bugetară', dfs: 'Dosare ALOP', df: 'Dosar ALOP', einvoices: 'Facturi RO e-Factura', orders: 'Ordonanțări și plăți', budget: 'Buget', registers: 'Registre și rapoarte', settings: 'Setări ALOP', map: 'Investiții pe hartă' };

/** Badges of the sidebar: my tasks, invoices waiting. Refreshed on changes and every minute. */
export function useAlopBadges(active: boolean) {
  const [b, setB] = useState({ tasks: 0, einv: 0 });
  useEffect(() => {
    if (!active) return;
    const load = () => api.get<Task[]>('/api/alop/tasks').then(t => setB({ tasks: t.filter(x => x.entity !== 'EINV').length, einv: t.filter(x => x.entity === 'EINV').reduce((s, x) => s + (parseInt(x.title) || 0), 0) })).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('alop:changed', load);
    return () => { clearInterval(t); window.removeEventListener('alop:changed', load); };
  }, [active]);
  return b;
}

type NavItemFn = (p: { icon: string; label: string; active: boolean; onClick: () => void; count?: number; alert?: boolean; title?: string }) => React.ReactElement;

export function AlopNav({ page, NavItem }: { page: string; NavItem: NavItemFn }) {
  const { isAdmin } = useAuth();
  const me = useAlopMe();
  const b = useAlopBadges(true);
  return <>
    <div className="nav-section">Lucru</div>
    <NavItem icon="inbox" label="De făcut" count={b.tasks} active={page === 'tasks'} onClick={() => navigate('/alop/tasks')} />
    <NavItem icon="folder" label="Dosare" active={page === 'dfs' || page === 'df'} onClick={() => navigate('/alop/dfs')} />
    {(me?.roles.ALOP_ECONOMIC || me?.roles.ALOP_INITIATOR) && <NavItem icon="receipt" label="Facturi e-Factura" count={b.einv} active={page === 'einvoices'} onClick={() => navigate('/alop/einvoices')} />}
    <NavItem icon="bank" label="Ordonanțări și plăți" active={page === 'orders'} onClick={() => navigate('/alop/orders')} />
    <div className="nav-section">Buget</div>
    <NavItem icon="chart" label="Execuție" active={page === 'dashboard'} onClick={() => navigate('/alop/dashboard')} />
    <NavItem icon="coins" label="Buget" active={page === 'budget'} onClick={() => navigate('/alop/budget')} />
    <NavItem icon="map" label="Investiții pe hartă" active={page === 'map'} onClick={() => navigate('/alop/map')} />
    <NavItem icon="report" label="Rapoarte și registre" active={page === 'reports' || page === 'registers'} onClick={() => navigate('/reports?m=ALOP')} />
    <NavItem icon="users" label="Delegări" active={page === 'delegations'} onClick={() => navigate('/delegations')} />
    {isAdmin && <><div className="nav-section">Configurare</div><NavItem icon="settings" label="Setări și conectori" active={page === 'settings'} onClick={() => navigate('/alop/settings')} /></>}
  </>;
}

export function AlopContent({ parts }: { parts: string[] }) {
  const page = parts[1] || 'tasks';
  const { isAdmin } = useAuth();
  const me = useAlopMe();
  const [creating, setCreating] = useState(false);
  // from a procurement dossier: #/alop/dfs?new=1&paap=<position>
  const route = useRoute();
  const paapNew = route.params.get('new') && route.params.get('paap') ? Number(route.params.get('paap')) : null;
  useEffect(() => { if (paapNew) setCreating(true); }, [paapNew]);
  // N: new document de fundamentare, from anywhere in the module
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey && !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) && !t.isContentEditable && !document.querySelector('.backdrop') && me?.roles.ALOP_INITIATOR) { e.preventDefault(); setCreating(true); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [me]);
  if (me && !me.access) return <div className="empty"><Icon name="lock" /><h3>Nu aveți acces la ALOP</h3><p>Administratorul vă poate da unul dintre rolurile ALOP din Administrare → Utilizatori.</p></div>;
  return <>
    {page === 'tasks' && <Tasks />}
    {page === 'dashboard' && <Dashboard />}
    {page === 'dfs' && <Dossiers />}
    {page === 'df' && parts[2] && <Dossier key={parts[2]} id={Number(parts[2])} />}
    {page === 'einvoices' && <EInvoices />}
    {page === 'orders' && <Orders />}
    {page === 'budget' && <Budget />}
    {page === 'map' && <InvestmentsMap />}
    {page === 'registers' && <RedirectToReports />}
    {page === 'settings' && isAdmin && <Settings />}
    {creating && <DfForm prefill={paapNew ? { paapPositionId: paapNew, category: 'ACHIZITIE' } : undefined} onClose={() => { setCreating(false); if (paapNew) navigate('/alop/dfs'); }} onSaved={d => { setCreating(false); navigate('/alop/df/' + d.id); }} />}
  </>;
}

export type ModuleKey = 'dms' | 'alop' | 'urb' | 'ach' | 'cl';
const MOD_HOME: Record<ModuleKey, string> = { dms: '/registry', alop: '/alop/tasks', urb: '/urbanism/tasks', ach: '/achizitii', cl: '/consiliu/tasks' };
const modOf = (path: string): ModuleKey => path.startsWith('/alop') ? 'alop' : path.startsWith('/urbanism') ? 'urb' : path.startsWith('/achizitii') ? 'ach' : path.startsWith('/consiliu') ? 'cl' : 'dms';

/** DMS | ALOP | Urbanism | Achiziții | Consiliul local — the modules of the product the user may open. Remembers the last page of each module. */
export function ModuleSwitch({ current, modules }: { current: ModuleKey; modules: ModuleKey[] }) {
  const go = (m: ModuleKey) => {
    if (m === current) return;
    try { localStorage.setItem('docflow.last.' + current, location.hash.replace(/^#/, '')); } catch { /* ignore */ }
    let last: string | null = null;
    try { last = localStorage.getItem('docflow.last.' + m); } catch { /* ignore */ }
    navigate(last && modOf(last) === m ? last : MOD_HOME[m]);
  };
  const ALL: { k: ModuleKey; icon: string; label: string; title: string }[] = [
    { k: 'dms', icon: 'files', label: 'DMS', title: 'Registratură și documente' },
    { k: 'alop', icon: 'coins', label: 'ALOP', title: 'Angajare, lichidare, ordonanțare și plată' },
    { k: 'urb', icon: 'building', label: 'Urbanism', title: 'Certificate de urbanism, autorizații de construire, notificări — Legea nr. 169/2026' },
    { k: 'ach', icon: 'clipboard', label: 'Achiziții', title: 'Programul anual al achizițiilor publice, strategia, dosarele achizițiilor — Legea nr. 98/2016' },
    { k: 'cl', icon: 'book', label: 'Consiliu', title: 'Consiliul local: proiecte de hotărâri, ședințe, hotărâri și dispoziții — Codul administrativ' },
  ];
  return (
    <div className="module-switch" role="tablist" aria-label="Modul">
      {ALL.filter(m => modules.includes(m.k)).map(m => (
        <button key={m.k} role="tab" aria-selected={current === m.k} className={(current === m.k ? 'active ' : '') + m.k} onClick={() => go(m.k)} title={m.title}><Icon name={m.icon} size={15} /><span>{m.label}</span></button>
      ))}
    </div>
  );
}

/** The registers moved to the common "Rapoarte" section. */
function RedirectToReports() { useEffect(() => { navigate('/reports?m=ALOP&r=cfp'); }, []); return null; }
